import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Binary, Bug, Building2, ClipboardCheck, Inbox, LayoutGrid, Megaphone, ShieldCheck, SlidersHorizontal, Tags, Users } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { MachinesGateway } from '@forq/machines-data';
import type { MastersGateway } from '@forq/masters-data';
import type { AttachmentDocumentGateway, BugReportGateway, BugReportRecord, BugStatus, CompanyProfileGateway } from '@forq/platform-data';
import { EmptyState } from '@forq/ui';
import { AccessRequestsFeature } from './features/AccessRequestsFeature';
import { AnnouncementsFeature } from './features/AnnouncementsFeature';
import { AttachmentDocumentsFeature } from './features/AttachmentDocumentsFeature';
import { AuditFeature } from './features/AuditFeature';
import { BugsFeature } from './features/BugsFeature';
import { CompanyFeature } from './features/CompanyFeature';
import { MachineTypesFeature } from './features/MachineTypesFeature';
import { MastersFeature } from './features/MastersFeature';
import { NumberingFeature } from './features/NumberingFeature';
import { OverviewFeature } from './features/OverviewFeature';
import { RolesFeature } from './features/RolesFeature';
import { UsersFeature } from './features/UsersFeature';
import type {
  AdminGateway,
  AdminSection,
  AdminSnapshot,
  InviteAdminUserInput,
  SaveAnnouncementInput,
} from './types';

interface AdminPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: AdminGateway;
  readonly machinesGateway: MachinesGateway;
  readonly mastersGateway: MastersGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly attachmentDocumentGateway: AttachmentDocumentGateway;
  readonly bugGateway: BugReportGateway;
}

const sections: readonly {
  readonly id: AdminSection;
  readonly featureId: string;
  readonly label: string;
  readonly icon: typeof LayoutGrid;
}[] = [
  { id: 'overview', featureId: 'admin.overview', label: 'Übersicht', icon: LayoutGrid },
  { id: 'company', featureId: 'admin.company', label: 'Grundkonfiguration', icon: Building2 },
  { id: 'documents', featureId: 'admin.documents', label: 'Prüflisten & Dokumente', icon: ClipboardCheck },
  { id: 'users', featureId: 'admin.users', label: 'Benutzer', icon: Users },
  { id: 'access-requests', featureId: 'admin.access-requests', label: 'Zugangsanfragen', icon: Inbox },
  { id: 'announcements', featureId: 'admin.announcements', label: 'Systemmeldungen', icon: Megaphone },
  { id: 'bugs', featureId: 'admin.bugs', label: 'Fehlermeldungen', icon: Bug },
  { id: 'roles', featureId: 'admin.roles', label: 'Rollen & Rechte', icon: ShieldCheck },
  { id: 'numbering', featureId: 'admin.numbering', label: 'Nummernkreise', icon: Binary },
  { id: 'machine-types', featureId: 'admin.machine-types', label: 'Maschinentypen', icon: SlidersHorizontal },
  { id: 'masters', featureId: 'admin.masters', label: 'Kostenstellen & Warengruppen', icon: Tags },
  { id: 'audit', featureId: 'admin.audit', label: 'Audit', icon: Activity },
];

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Administrationsdaten konnten nicht geladen werden.';
}

export function AdminPage({
  tenant,
  mode,
  permissions,
  enabledFeatureIds,
  gateway,
  machinesGateway,
  mastersGateway,
  companyGateway,
  attachmentDocumentGateway,
  bugGateway,
}: AdminPageProps) {
  const availableSections = useMemo(
    () => sections.filter((section) => enabledFeatureIds.includes(section.featureId)),
    [enabledFeatureIds],
  );
  const [activeSection, setActiveSection] = useState<AdminSection>(availableSections[0]?.id ?? 'overview');
  const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [busyRoleUserId, setBusyRoleUserId] = useState<string | null>(null);
  const [busyPermissionKey, setBusyPermissionKey] = useState<string | null>(null);
  const [busySequenceId, setBusySequenceId] = useState<string | null>(null);
  const [decidingRequestId, setDecidingRequestId] = useState<string | null>(null);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [inviting, setInviting] = useState(false);

  // Fehlermeldungen hängen NICHT am Schnappschuss: sie kommen aus einer eigenen
  // Quelle, und wer sie bearbeitet, soll nicht bei jedem Klick die ganze
  // Administration nachladen.
  const [bugReports, setBugReports] = useState<readonly BugReportRecord[]>([]);
  const [busyBugId, setBusyBugId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await gateway.loadSnapshot(tenant.id);
      setSnapshot(next);
      setSelectedRoleId((current) => next.roles.some((role) => role.id === current) ? current : next.roles[0]?.id ?? '');
    } catch (loadError) {
      setSnapshot(null);
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, tenant.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!availableSections.some((section) => section.id === activeSection)) {
      setActiveSection(availableSections[0]?.id ?? 'overview');
    }
  }, [activeSection, availableSections]);

  // Erst laden, wenn der Abschnitt offen ist. Wer die Administration wegen der
  // Nummernkreise aufmacht, braucht keine Fehlerliste.
  useEffect(() => {
    if (activeSection !== 'bugs') return;
    let aktiv = true;
    bugGateway.listAll(tenant.id)
      .then((geladen) => { if (aktiv) setBugReports(geladen); })
      .catch((ladefehler: unknown) => { if (aktiv) setError(messageOf(ladefehler)); });
    return () => { aktiv = false; };
  }, [activeSection, bugGateway, tenant.id]);

  const setBugStatus = useCallback(async (reportId: string, status: BugStatus, note: string) => {
    setBusyBugId(reportId);
    setError(null);
    try {
      setBugReports(await bugGateway.setStatus(tenant.id, reportId, status, note));
    } catch (statusError) {
      setError(messageOf(statusError));
    } finally {
      setBusyBugId(null);
    }
  }, [bugGateway, tenant.id]);

  const inviteUser = useCallback(async (
    input: Omit<InviteAdminUserInput, 'tenantId'>,
  ): Promise<string | null> => {
    setInviting(true);
    setError(null);
    try {
      // Das Anfangspasswort kommt nur von einem Server ohne Mailversand und
      // wird bewusst nur weitergereicht, nicht gespeichert.
      const outcome = await gateway.inviteUser({ ...input, tenantId: tenant.id });
      setSnapshot(outcome.snapshot);
      return outcome.initialPassword;
    } catch (inviteError) {
      setError(messageOf(inviteError));
      throw inviteError;
    } finally {
      setInviting(false);
    }
  }, [gateway, tenant.id]);

  const updateUser = useCallback(async (userId: string, active: boolean) => {
    setBusyUserId(userId);
    setError(null);
    try {
      setSnapshot(await gateway.setUserActive(tenant.id, userId, active));
    } catch (updateError) {
      setError(messageOf(updateError));
    } finally {
      setBusyUserId(null);
    }
  }, [gateway, tenant.id]);

  const updateUserRoles = useCallback(async (userId: string, roleIds: readonly string[]) => {
    setBusyRoleUserId(userId);
    setError(null);
    try {
      setSnapshot(await gateway.setUserRoles(tenant.id, userId, roleIds));
    } catch (updateError) {
      setError(messageOf(updateError));
    } finally {
      setBusyRoleUserId(null);
    }
  }, [gateway, tenant.id]);

  const assignUser = useCallback(async (userId: string, roleIds: readonly string[]) => {
    setBusyRoleUserId(userId);
    setError(null);
    try {
      setSnapshot(await gateway.assignUser(tenant.id, userId, roleIds));
    } catch (assignError) {
      setError(messageOf(assignError));
    } finally {
      setBusyRoleUserId(null);
    }
  }, [gateway, tenant.id]);

  const updatePermission = useCallback(async (roleId: string, permissionKey: string, granted: boolean) => {
    setBusyPermissionKey(permissionKey);
    setError(null);
    try {
      setSnapshot(await gateway.setRolePermission(tenant.id, roleId, permissionKey, granted));
    } catch (updateError) {
      setError(messageOf(updateError));
    } finally {
      setBusyPermissionKey(null);
    }
  }, [gateway, tenant.id]);

  const decideRequest = useCallback(async (requestId: string, approve: boolean, note: string) => {
    setDecidingRequestId(requestId);
    setError(null);
    try {
      setSnapshot(await gateway.decideAccessRequest({ tenantId: tenant.id, requestId, approve, note }));
    } catch (decideError) {
      setError(messageOf(decideError));
    } finally {
      setDecidingRequestId(null);
    }
  }, [gateway, tenant.id]);

  const saveAnnouncement = useCallback(async (input: Omit<SaveAnnouncementInput, 'tenantId'>) => {
    setSavingAnnouncement(true);
    setError(null);
    try {
      setSnapshot(await gateway.saveAnnouncement({ ...input, tenantId: tenant.id }));
    } finally {
      // Bewusst kein catch: die Flaeche zeigt den Fehler direkt am Formular, wo der Text noch
      // steht. Ihn hier oben zu schlucken hiesse, den Entwurf mit einer Meldung woanders zu quittieren.
      setSavingAnnouncement(false);
    }
  }, [gateway, tenant.id]);

  const deleteAnnouncement = useCallback(async (announcementId: string) => {
    setSavingAnnouncement(true);
    setError(null);
    try {
      setSnapshot(await gateway.deleteAnnouncement(tenant.id, announcementId));
    } catch (deleteError) {
      setError(messageOf(deleteError));
    } finally {
      setSavingAnnouncement(false);
    }
  }, [gateway, tenant.id]);

  const updateSequence = useCallback(async (sequenceId: string, active: boolean) => {
    setBusySequenceId(sequenceId);
    setError(null);
    try {
      setSnapshot(await gateway.setNumberSequenceActive(tenant.id, sequenceId, active));
    } catch (updateError) {
      setError(messageOf(updateError));
    } finally {
      setBusySequenceId(null);
    }
  }, [gateway, tenant.id]);

  if (availableSections.length === 0) {
    return (
      <EmptyState
        eyebrow="Keine Administrationsrechte"
        title="Für diesen Mandanten ist keine Admin-Funktion freigegeben."
        description="Die sichtbaren Funktionen werden aus Rollen und Benutzer-Ausnahmen ermittelt."
      />
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <div>
          <span className="admin-eyebrow">FORQ Administration</span>
          <h1>System einfach verwalten</h1>
          <p>{tenant.name} · Mandant {tenant.numberPrefix}xxxx</p>
        </div>
        {/* Nicht "Supabase": dieselbe Anwendung laeuft inzwischen auch gegen
            den eigenen Windows-Server, und dort waere der Name schlicht
            falsch. Die Aussage, auf die es ankommt, ist ohnehin eine andere --
            arbeite ich auf echten Daten oder nicht. */}
        <span className={`admin-mode-badge ${mode === 'demo' ? 'is-demo' : ''}`}>
          {mode === 'demo' ? 'Demo – Änderungen nur bis zum Neuladen' : 'Mit der Datenbank verbunden'}
        </span>
      </header>

      {error && (
        <div className="admin-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => void load()}>Erneut laden</button>
        </div>
      )}

      <nav className="admin-section-nav" aria-label="Administrationsbereiche">
        {availableSections.map(({ id, label, icon: Icon }) => (
          <button
            type="button"
            key={id}
            className={activeSection === id ? 'is-active' : ''}
            onClick={() => setActiveSection(id)}
          >
            <Icon size={17} />
            {label}
          </button>
        ))}
      </nav>

      {loading && <div className="admin-loading">Administrationsdaten werden geladen …</div>}

      {!loading && snapshot && (
        <div className="admin-page__content">
          {activeSection === 'overview' && <OverviewFeature snapshot={snapshot} />}
          {activeSection === 'company' && (
            <CompanyFeature tenant={tenant} gateway={companyGateway} canEdit={permissions.has('admin.view')} />
          )}
          {activeSection === 'documents' && (
            <AttachmentDocumentsFeature tenant={tenant} gateway={attachmentDocumentGateway} canEdit={permissions.has('admin.view')} />
          )}
          {activeSection === 'access-requests' && (
            <AccessRequestsFeature
              snapshot={snapshot}
              canDecide={permissions.has('admin.users.manage')}
              busy={decidingRequestId !== null}
              onDecide={decideRequest}
            />
          )}
          {activeSection === 'bugs' && (
            <BugsFeature
              reports={bugReports}
              canManage={permissions.has('admin.bugs.manage')}
              busyId={busyBugId}
              onSetStatus={(reportId, status, note) => { void setBugStatus(reportId, status, note); }}
            />
          )}
          {activeSection === 'announcements' && (
            <AnnouncementsFeature
              // Mandantenwechsel = neue Komponente = frischer Entwurf. Sonst tippt man die Meldung
              // fuer FTW, wechselt die Firma und speichert sie bei Spezialtransporte.
              key={snapshot.tenantId}
              snapshot={snapshot}
              canManage={permissions.has('admin.tenants.manage')}
              busy={savingAnnouncement}
              onSave={saveAnnouncement}
              onDelete={deleteAnnouncement}
            />
          )}
          {activeSection === 'users' && (
            <UsersFeature
              users={snapshot.users}
              invitations={snapshot.invitations}
              unassignedUsers={snapshot.unassignedUsers}
              roles={snapshot.roles}
              canManage={permissions.has('admin.users.manage')}
              busyUserId={busyUserId}
              busyRoleUserId={busyRoleUserId}
              inviting={inviting}
              onInvite={inviteUser}
              onToggleActive={(userId, active) => void updateUser(userId, active)}
              onSetRoles={(userId, roleIds) => void updateUserRoles(userId, roleIds)}
              onAssign={(userId, roleIds) => void assignUser(userId, roleIds)}
            />
          )}
          {activeSection === 'roles' && (
            <RolesFeature
              roles={snapshot.roles}
              permissionGroups={snapshot.permissionGroups}
              selectedRoleId={selectedRoleId}
              canManage={permissions.has('admin.roles.manage')}
              busyPermissionKey={busyPermissionKey}
              onSelectRole={setSelectedRoleId}
              onTogglePermission={(roleId, permissionKey, granted) => void updatePermission(roleId, permissionKey, granted)}
            />
          )}
          {activeSection === 'numbering' && (
            <NumberingFeature
              sequences={snapshot.numberSequences}
              canManage={permissions.has('admin.numbering.manage')}
              busySequenceId={busySequenceId}
              onToggleActive={(sequenceId, active) => void updateSequence(sequenceId, active)}
            />
          )}
          {activeSection === 'machine-types' && (
            <MachineTypesFeature
              tenantId={tenant.id}
              tenantName={tenant.name}
              gateway={machinesGateway}
              canManage={permissions.has('machines.types.manage')}
            />
          )}
          {activeSection === 'masters' && (
            <MastersFeature
              key={snapshot.tenantId}
              tenantId={tenant.id}
              gateway={mastersGateway}
              canManage={permissions.has('admin.tenants.manage')}
            />
          )}
          {activeSection === 'audit' && <AuditFeature records={snapshot.audit} />}
        </div>
      )}
    </div>
  );
}
